/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Agoric Iframe Handler
 *
 * Manages communication with the Agoric sandbox iframe.
 * Handles message passing, timeouts, and error handling.
 */

type MessageType =
  | "CONNECT_WALLET"
  | "FUND_SURVEY"
  | "CLAIM_REWARDS"
  | "GET_STATUS"
  | "SIGN_DATA"
  | "SPONSOR_PARTICIPANT_GAS";

interface AgoricMessage {
  type: MessageType;
  data?: any;
  id: string;
}

interface AgoricResponse {
  type: "AGORIC_RESPONSE";
  id: string;
  success: boolean;
  data?: any;
  error?: {
    code: string;
    message: string;
  };
}

// Load and validate env var before class instantiation
const AGORIC_IFRAME_URL = (() => {
  const url = process.env.REACT_APP_AGORIC_IFRAME_URL;
  if (!url) {
    throw new Error("REACT_APP_AGORIC_IFRAME_URL is not set. " + "Please add it to your .env file. ");
  }
  return url;
})();

export class AgoricIframeHandler {
  private iframe: HTMLIFrameElement | null = null;
  private isReady = false;
  private readyPromise: Promise<void>;
  private readyResolve: (() => void) | null = null;
  private targetOrigin: string;
  private pendingMessages = new Map<
    string,
    {
      resolve: (value: any) => void;
      reject: (error: any) => void;
      timeout: NodeJS.Timeout;
    }
  >();

  constructor(private sandboxUrl = AGORIC_IFRAME_URL) {
    // Extract origin from sandbox URL for secure postMessage
    try {
      const url = new URL(this.sandboxUrl, window.location.origin);
      this.targetOrigin = url.origin;
    } catch {
      // If relative URL, use same origin
      this.targetOrigin = window.location.origin;
    }

    // Create promise for iframe ready state
    this.readyPromise = new Promise(resolve => {
      this.readyResolve = resolve;
    });

    // Listen for messages from iframe
    window.addEventListener("message", this.handleMessage);
  }

  /**
   * Initialize the iframe and load the sandbox
   */
  init(): void {
    if (this.iframe) {
      console.log("[Agoric Handler] Already initialized");
      return;
    }

    console.log("[Agoric Handler] Initializing iframe...");

    // Create iframe
    // WARNING: This will load SES lockdown which hardens JavaScript built-ins
    this.iframe = document.createElement("iframe");
    this.iframe.src = this.sandboxUrl;

    // Hide iframe completely (it's only for background communication)
    this.iframe.style.display = "none";

    // Need allow-same-origin for postMessage to work with external CDN
    this.iframe.sandbox.add("allow-scripts", "allow-popups", "allow-same-origin");

    // Add load and error listeners
    this.iframe.onload = () => {
      console.log("[Agoric Handler] Iframe loaded successfully");
    };
    this.iframe.onerror = error => {
      console.error("[Agoric Handler] Iframe failed to load:", error);
      // Reject the ready promise if iframe fails to load
      if (this.readyResolve) {
        this.readyResolve = null;
      }
      // Reject all pending messages with iframe load error
      for (const [_, pending] of this.pendingMessages.entries()) {
        clearTimeout(pending.timeout);
        pending.reject(new Error("IFRAME_LOAD_FAILED"));
      }
      this.pendingMessages.clear();
    };

    document.body.appendChild(this.iframe);

    console.log("[Agoric Handler] Iframe created, loading from:", this.sandboxUrl);
  }

  /**
   * Wait for the iframe to be ready
   */
  async waitForReady(timeout = 10000): Promise<void> {
    const timeoutPromise = new Promise<void>((_, reject) => {
      setTimeout(() => reject(new Error("SANDBOX_TIMEOUT")), timeout);
    });

    try {
      await Promise.race([this.readyPromise, timeoutPromise]);
      console.log("[Agoric Handler] Iframe ready");
    } catch (error) {
      console.error("[Agoric Handler] Failed to initialize:", error);
      throw error;
    }
  }

  /**
   * Send a message to the iframe and wait for response
   */
  private async sendMessage(type: MessageType, data?: any, timeout = 1200000): Promise<any> {
    // Ensure iframe is initialized and ready
    if (!this.iframe) {
      this.init();
    }

    await this.waitForReady();

    return new Promise((resolve, reject) => {
      const id = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Set up timeout
      const timeoutId = setTimeout(() => {
        this.pendingMessages.delete(id);
        reject(new Error("MESSAGE_TIMEOUT"));
      }, timeout);

      // Store pending message handlers
      this.pendingMessages.set(id, {
        resolve,
        reject,
        timeout: timeoutId,
      });

      // Send message to iframe with proper origin
      const message: AgoricMessage = { type, data, id };
      this.iframe!.contentWindow!.postMessage(message, this.targetOrigin);

      console.log("[Agoric Handler] Sent message:", type, data, "to origin:", this.targetOrigin);
    });
  }

  /**
   * Handle messages from the iframe
   */
  private handleMessage = (event: MessageEvent) => {
    // Log all messages for debugging
    // console.log("[Agoric Handler] Received message:", event.data, "from:", event.origin);

    const message = event.data;

    // Handle ready message
    if (message.type === "AGORIC_READY") {
      console.log("[Agoric Handler] Received ready signal");
      this.isReady = true;
      if (this.readyResolve) {
        this.readyResolve();
      }
      return;
    }

    // Handle response messages
    if (message.type === "AGORIC_RESPONSE") {
      const response = message as AgoricResponse;
      const pending = this.pendingMessages.get(response.id);

      if (!pending) {
        console.warn("[Agoric Handler] Received response for unknown message:", response.id);
        return;
      }

      // Clear timeout
      clearTimeout(pending.timeout);
      this.pendingMessages.delete(response.id);

      // Resolve or reject based on response
      if (response.success) {
        console.log("[Agoric Handler] Message succeeded:", response.data);
        pending.resolve(response.data);
      } else {
        console.error("[Agoric Handler] Message failed:", response.error);
        // Reject with the error object from iframe (contains code and message)
        const error = new Error(response.error?.message || "Unknown error") as Error & { code?: string };
        if (response.error?.code) {
          error.code = response.error.code;
        }
        pending.reject(error);
      }
    }
  };

  /**
   * Connect to Keplr wallet
   */
  async connectWallet(network?: string): Promise<{ address: string; pubkey: { type: string; value: any } | null }> {
    console.log("Connecting to wallet with network:", network);
    return await this.sendMessage("CONNECT_WALLET", { network });
  }

  /**
   * Fund a survey with Agoric tokens
   */
  async fundSurvey(params: {
    surveyId: string;
    messages: any[];
    denom: string;
    totalAmount: string;
  }): Promise<{ success: boolean; txHash: string; offerId: number }> {
    console.log("Funding survey...", params);
    return await this.sendMessage("FUND_SURVEY", params);
  }

  /**
   * Claim rewards from a survey
   */
  async claimRewards(params: {
    surveyId: string;
    messages: any[];
    denom: string;
    totalAmount: string;
  }): Promise<{ success: boolean; txHash: string; offerId: number }> {
    return await this.sendMessage("CLAIM_REWARDS", params);
  }

  /**
   * Sign data with wallet
   */
  async signData(params: { data: string }): Promise<{ signedData: any; signature: any }> {
    return await this.sendMessage("SIGN_DATA", params);
  }

  /**
   * Sponsor participant gas fees
   * Sends tokens from user's wallet to the backend sponsor wallet
   */
  async sponsorParticipantGas(params: {
    destinationAddress: string;
    amount: string;
    denom: string;
  }): Promise<{ success: boolean; txHash: string }> {
    console.log("Sponsoring participant gas...", params);
    return await this.sendMessage("SPONSOR_PARTICIPANT_GAS", params);
  }

  /**
   * Get current connection status
   */
  async getStatus(): Promise<{
    connected: boolean;
    address: string | null;
    hasAccount: boolean;
    accountInvitationId: string | null;
    initialized: boolean;
    hasBrands: boolean;
    hasInstance: boolean;
    brandsAvailable: string[];
    network: string;
  }> {
    return await this.sendMessage("GET_STATUS");
  }

  /**
   * Clean up the iframe and listeners
   */
  destroy(): void {
    console.log("[Agoric Handler] Cleaning up...");

    // Remove message listener
    window.removeEventListener("message", this.handleMessage);

    // Clear pending messages
    for (const [_, pending] of this.pendingMessages.entries()) {
      clearTimeout(pending.timeout);
      pending.reject(new Error("HANDLER_DESTROYED"));
    }
    this.pendingMessages.clear();

    // Remove iframe
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }

    this.iframe = null;
    this.isReady = false;
  }
}

// Singleton instance - lazy initialization
// The iframe will only be created when first needed
let agoricHandler: AgoricIframeHandler | null = null;

/**
 * Get the singleton Agoric handler instance
 * Creates and initializes the iframe on first call (lazy initialization)
 */
export function getAgoricHandler(): AgoricIframeHandler {
  if (!agoricHandler) {
    console.log("[Agoric] Creating handler instance...");
    agoricHandler = new AgoricIframeHandler();
    // Don't auto-init - let first sendMessage() trigger it
  }
  return agoricHandler;
}

/**
 * Clean up the Agoric handler (for testing or cleanup)
 */
export function destroyAgoricHandler(): void {
  if (agoricHandler) {
    agoricHandler.destroy();
    agoricHandler = null;
  }
}
